<template>
  <div
    ref="rootEl"
    class="cs-info-popover"
    @mouseenter="onMouseEnter"
    @mouseleave="onMouseLeave"
  >
    <button
      ref="triggerEl"
      type="button"
      class="cs-info-trigger"
      :class="{ 'cs-info-trigger--icon': isIconTrigger }"
      :aria-label="ariaLabel"
      aria-haspopup="dialog"
      :aria-expanded="isOpen ? 'true' : 'false'"
      @click="onClick"
      @keydown.enter="onClick"
      @keydown.space.prevent="onClick"
      @focus="onFocus"
      @blur="onBlur"
    >
      <slot name="trigger">
        <i class="fas fa-info-circle" aria-hidden="true"></i>
      </slot>
    </button>

    <transition name="cs-popover">
      <div
        v-if="isOpen"
        class="cs-popover-box"
        :class="{ 'cs-popover-box--right': alignRight }"
        role="dialog"
        aria-modal="false"
        :aria-label="title"
      >
        <div v-if="title" class="popover-header">
          <h5 class="popover-heading">{{ title }}</h5>
          <button
            type="button"
            class="popover-close"
            aria-label="Close explainer"
            @click="close(false)"
          >
            &times;
          </button>
        </div>
        <div class="popover-body">
          <p
            v-for="(def, i) in definitions"
            :key="i"
            class="deal-def-item"
          >
            <strong v-if="def.label">{{ def.label }}:</strong>
            <template v-if="def.label"> </template>{{ def.body }}
          </p>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
let hoverTimer = null;
const HOVER_DELAY_MS = 150;

export default {
  name: 'DealExplainerPopover',
  props: {
    title: {
      type: String,
      default: ''
    },
    definitions: {
      type: Array,
      default: () => []
    },
    ariaLabel: {
      type: String,
      default: 'Deal definition and methodology'
    },
    alignRight: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      isOpen: false,
      pinned: false
    };
  },
  computed: {
    isIconTrigger() {
      return !(this.$slots && this.$slots.trigger);
    }
  },
  mounted() {
    document.addEventListener('click', this.onDocumentClick);
    document.addEventListener('keydown', this.onDocumentKeydown);
  },
  beforeUnmount() {
    this.clearHoverTimer();
    document.removeEventListener('click', this.onDocumentClick);
    document.removeEventListener('keydown', this.onDocumentKeydown);
  },
  methods: {
    clearHoverTimer() {
      if (hoverTimer) {
        clearTimeout(hoverTimer);
        hoverTimer = null;
      }
    },
    onMouseEnter() {
      // Hover preview with 150ms delay; moving into the popover itself keeps
      // it open because these handlers live on the wrapping container.
      this.clearHoverTimer();
      hoverTimer = setTimeout(() => {
        this.isOpen = true;
      }, HOVER_DELAY_MS);
    },
    onMouseLeave() {
      this.clearHoverTimer();
      if (!this.pinned) {
        this.isOpen = false;
      }
    },
    onFocus() {
      this.clearHoverTimer();
      this.isOpen = true;
    },
    onBlur() {
      if (!this.pinned) {
        this.isOpen = false;
      }
    },
    onClick() {
      // Click/Enter/Space pin the popover open (keyboard + mobile tap toggle).
      this.clearHoverTimer();
      if (this.isOpen && this.pinned) {
        this.close(false);
      } else {
        this.pinned = true;
        this.isOpen = true;
      }
    },
    close(restoreFocus) {
      this.clearHoverTimer();
      this.pinned = false;
      this.isOpen = false;
      if (restoreFocus) {
        this.$nextTick(() => {
          if (this.$refs.triggerEl) this.$refs.triggerEl.focus();
        });
      }
    },
    onDocumentClick(event) {
      if (this.isOpen && this.$refs.rootEl && !this.$refs.rootEl.contains(event.target)) {
        this.close(false);
      }
    },
    onDocumentKeydown(event) {
      if (event.key === 'Escape' && this.isOpen) {
        this.close(true);
      }
    }
  }
};
</script>

<style scoped>
.cs-info-popover {
  position: relative;
  display: inline-flex;
}

.cs-info-trigger {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: var(--space-2);
  padding: 0;
  border: none;
  background: transparent;
  color: inherit;
  font: inherit;
  line-height: 1;
  cursor: pointer;
  flex-shrink: 0;
}

.cs-info-trigger--icon {
  width: 24px;
  height: 24px;
  border: 1px solid var(--cs-card-border);
  border-radius: var(--radius-pill);
  background: var(--cs-signal-mist);
  color: var(--cs-signal-teal);
  font-size: 13px;
}

/* Expand to a 44px touch target without changing the visual hit area */
.cs-info-trigger::before {
  content: '';
  position: absolute;
  inset: -10px;
  border-radius: var(--radius-pill);
}

.cs-info-trigger--icon:hover,
.cs-info-trigger--icon[aria-expanded='true'] {
  background: var(--cs-card-border);
  color: var(--cs-signal-deep);
}

.cs-info-trigger:focus-visible {
  outline: 2px solid var(--cs-signal-teal);
  outline-offset: 2px;
}

.cs-popover-box {
  position: absolute;
  top: calc(100% + 8px);
  left: 50%;
  transform: translateX(-50%);
  width: 320px;
  max-width: calc(100vw - 32px);
  background: var(--cs-card);
  border: 1px solid var(--cs-card-border);
  border-radius: var(--radius-md);
  box-shadow: var(--shadow-card-hover);
  padding: var(--space-3) var(--space-4);
  z-index: 100;
}

/* Caret pointing at the trigger */
.cs-popover-box::before {
  content: '';
  position: absolute;
  top: -6px;
  left: 50%;
  transform: translateX(-50%) rotate(45deg);
  width: 10px;
  height: 10px;
  background: var(--cs-card);
  border-left: 1px solid var(--cs-card-border);
  border-top: 1px solid var(--cs-card-border);
}

/* Right-aligned (anchored to the trigger's right edge — for header badges) */
.cs-popover-box--right {
  left: auto;
  right: 0;
  transform: none;
}

.cs-popover-box--right::before {
  left: auto;
  right: 20px;
  transform: rotate(45deg);
}

.cs-popover-box--right.cs-popover-enter-from,
.cs-popover-box--right.cs-popover-leave-to {
  transform: translateY(-4px);
}

.popover-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-2);
  margin-bottom: var(--space-2);
}

.popover-heading {
  margin: 0;
  font-size: var(--text-small);
  font-family: var(--font-heading);
  font-weight: 700;
  color: var(--cs-ink);
}

.popover-close {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  padding: 0;
  border: none;
  border-radius: var(--radius-pill);
  background: transparent;
  color: var(--cs-muted);
  font-size: 20px;
  line-height: 1;
  cursor: pointer;
  flex-shrink: 0;
}

.popover-close:hover {
  background: var(--cs-signal-mist);
  color: var(--cs-signal-teal);
}

.popover-body {
  max-height: 60vh;
  overflow-y: auto;
}

.deal-def-item {
  margin: 0 0 var(--space-2);
  font-size: var(--text-small);
  line-height: var(--line-height-body);
  color: var(--cs-muted);
}

.deal-def-item:last-child {
  margin-bottom: 0;
}

.deal-def-item strong {
  color: var(--cs-ink);
}

.cs-popover-enter-active,
.cs-popover-leave-active {
  transition: opacity var(--transition-fast), transform var(--transition-fast);
}

.cs-popover-enter-from,
.cs-popover-leave-to {
  opacity: 0;
  transform: translateX(-50%) translateY(-4px);
}

/* Mobile: full-width panel under the trigger (bottom-sheet-like, no clipping) */
@media (max-width: 480px) {
  .cs-popover-box {
    left: 0;
    right: auto;
    transform: none;
    width: calc(100vw - 32px);
  }

  .cs-popover-box::before {
    left: 24px;
    transform: rotate(45deg);
  }

  .cs-popover-enter-from,
  .cs-popover-leave-to {
    transform: translateY(-4px);
  }
}
</style>